// ════════════════════════════════════════════════════════════
// /depenses/fournisseurs — Sprint 3 Q5 (catalogue)
// ════════════════════════════════════════════════════════════
// - Liste fournisseurs avec search trigram (fuzzy)
// - Card par fournisseur : nom + NIF + nb factures + last facture + KYC
// - Action "Valider KYC" (daf only)
// - Visualisation defaults memorisés (categ + mode paiement + compte)
// ════════════════════════════════════════════════════════════
import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ArrowLeft,
  Search,
  Building2,
  CheckCircle2,
  Globe,
  ShieldCheck,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useFournisseurs, useValidateKyc } from '@/queries/depenses';
import { useComptesTresorerie } from '@/queries/tresorerie';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import type { Fournisseur } from '@ecr/core/depenses';

export const Route = createFileRoute('/_authed/depenses/fournisseurs')({
  staticData: { crumb: 'Fournisseurs' },
  component: FournisseursPage,
});

function FournisseursPage() {
  const [search, setSearch] = useState('');
  const { profile, session } = useAuth();
  const role = profile?.role ?? null;
  const userId = session?.user?.id ?? null;
  const isDaf = role === 'daf';

  const { data: fournisseurs = [], isLoading } = useFournisseurs({ search });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Link to="/depenses">
          <Button variant="ghost" size="icon" aria-label="Retour">
            <ArrowLeft className="size-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-foreground text-xl font-semibold sm:text-2xl">Fournisseurs</h1>
          <p className="text-muted-foreground text-sm">
            Catalogue + KYC + préférences mémorisées (auto-prefill 2e+ facture)
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
        <Input
          type="search"
          placeholder="Rechercher par nom ou NIF…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Liste */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-muted h-20 animate-pulse rounded" />
          ))}
        </div>
      ) : fournisseurs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <Building2 className="text-muted-foreground size-10" />
            <div>
              <p className="text-foreground font-medium">Aucun fournisseur</p>
              <p className="text-muted-foreground text-sm">
                {search
                  ? 'Pas de fournisseur correspondant à ta recherche.'
                  : 'Les fournisseurs sont créés automatiquement à la 1ère dépense.'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {fournisseurs.map((f) => (
            <FournisseurCard key={f.id} fournisseur={f} canValidateKyc={isDaf} userId={userId} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Fournisseur card ────────────────────────────────────
function FournisseurCard({
  fournisseur,
  canValidateKyc,
  userId,
}: {
  fournisseur: Fournisseur;
  canValidateKyc: boolean;
  userId: string | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const validateKyc = useValidateKyc();
  const { data: comptes = [] } = useComptesTresorerie();
  const defaultCompteLabel = fournisseur.defaultPaidCompte
    ? (comptes.find((c) => c.id === fournisseur.defaultPaidCompte)?.label ??
      fournisseur.defaultPaidCompte)
    : null;

  return (
    <Card>
      <CardContent className="space-y-2 py-3">
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="flex w-full items-start gap-3 text-left"
        >
          <div className="bg-primary/10 text-primary flex size-10 shrink-0 items-center justify-center rounded-full">
            <Building2 className="size-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <p className="text-foreground truncate font-medium">{fournisseur.nom}</p>
              {fournisseur.nifEtranger && (
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-950 dark:text-blue-200">
                  <Globe className="size-3" />
                  Étranger
                </span>
              )}
              {fournisseur.kycValidated ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-950 dark:text-green-200">
                  <ShieldCheck className="size-3" />
                  KYC
                </span>
              ) : (
                fournisseur.nbFactures <= 1 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800 dark:bg-orange-950 dark:text-orange-200">
                    <AlertTriangle className="size-3" />
                    Nouveau
                  </span>
                )
              )}
            </div>
            <p className="text-muted-foreground mt-0.5 text-xs">
              {fournisseur.nif ? `NIF ${fournisseur.nif} · ` : 'Sans NIF · '}
              {fournisseur.nbFactures} facture(s)
              {fournisseur.lastFactureAt && (
                <span>
                  {' '}
                  · dernière{' '}
                  {new Intl.DateTimeFormat('fr-FR', {
                    day: '2-digit',
                    month: 'short',
                  }).format(fournisseur.lastFactureAt)}
                </span>
              )}
            </p>
            {fournisseur.aliases.length > 0 && (
              <p className="text-muted-foreground mt-0.5 text-xs">
                Alias : {fournisseur.aliases.join(', ')}
              </p>
            )}
          </div>
        </button>

        {expanded && (
          <div className="border-border space-y-2 border-t pt-3 text-sm">
            <p className="text-muted-foreground text-xs font-medium uppercase">
              Préférences mémorisées (auto-prefill)
            </p>
            <Detail label="Catégorie par défaut" value={fournisseur.defaultCategorie} />
            <Detail
              label="Mode paiement par défaut"
              value={fournisseur.defaultModePaiement ?? null}
            />
            {fournisseur.defaultWalletType && (
              <Detail
                label="Wallet par défaut"
                value={`${fournisseur.defaultWalletType}${fournisseur.defaultWalletNumero ? ' — ' + fournisseur.defaultWalletNumero : ''}`}
              />
            )}
            {defaultCompteLabel && <Detail label="Compte par défaut" value={defaultCompteLabel} />}
            {fournisseur.defaultDevise && (
              <Detail label="Devise par défaut" value={fournisseur.defaultDevise} />
            )}

            {!fournisseur.kycValidated && canValidateKyc && (
              <div className="pt-2">
                <Button
                  type="button"
                  size="sm"
                  className="gap-2"
                  disabled={validateKyc.isPending}
                  onClick={() => {
                    validateKyc.mutate(
                      { fournisseurId: fournisseur.id, userId },
                      {
                        onSuccess: () => toast.success(`KYC validé pour ${fournisseur.nom}`),
                        onError: (e) =>
                          toast.error(`Erreur : ${e instanceof Error ? e.message : 'inconnu'}`),
                      },
                    );
                  }}
                >
                  <ShieldCheck className="size-4" />
                  Valider KYC
                </Button>
              </div>
            )}
            {fournisseur.kycValidated && fournisseur.kycValidatedAt && (
              <p className="text-muted-foreground text-xs">
                <CheckCircle2 className="inline size-3 text-green-600 dark:text-green-400" /> KYC
                validé le {new Intl.DateTimeFormat('fr-FR').format(fournisseur.kycValidatedAt)}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Detail({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className={cn('text-foreground text-right text-sm capitalize')}>{value}</span>
    </div>
  );
}
